import { ShieldAlert } from 'lucide-react'
import { Modal } from '@/components/Modal'
import { useThemeColors } from '@/hooks/useThemeColors'
import { FONT_HEADING, FONT_BODY, FONT_MONO } from '@/config/theme'
import type { CancelStatus } from '@/hooks/useCancelOrder'

/**
 * Cancel review (Batch B.1-polish ITEM 1) — the in-app confirmation shown BEFORE the wallet prompt,
 * matching the bridge/swap "Review transfer" panel (same Modal, same row layout). It renders EXACTLY the
 * fields the user is about to authorize (anti-phishing) — built from cancelDisplayFields with the nonce +
 * deadline that were minted when this modal opened, so what's shown is what gets signed.
 */
interface CancelReviewModalProps {
  isOpen: boolean
  /** Rows from cancelDisplayFields({ transferId, wallet, nonce, deadline, chain }). */
  rows: Array<{ label: string; value: string }>
  status: CancelStatus
  error?: string
  onConfirm: () => void
  onRetry: () => void
  onClose: () => void
}

export function CancelReviewModal({
  isOpen,
  rows,
  status,
  error,
  onConfirm,
  onRetry,
  onClose,
}: CancelReviewModalProps) {
  const c = useThemeColors()
  const busy = status === 'signing' || status === 'submitting'

  return (
    <Modal show={isOpen} onClose={onClose} title="Review cancellation" closable={!busy}>
      <div style={{ padding: '16px 20px 20px' }}>
        {/* Authorized fields card */}
        <div style={{
          background: c.iB, borderRadius: 16, padding: '14px 18px',
          border: `1px solid ${c.dv}`,
          display: 'flex', flexDirection: 'column', gap: 10,
        }}>
          {rows.map(r => (
            <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
              <span style={{ fontSize: 13, color: c.mu, fontFamily: FONT_BODY, flexShrink: 0 }}>{r.label}</span>
              <span style={{
                fontSize: 13, color: c.tx, fontWeight: 500, fontFamily: r.label === 'Order' ? FONT_MONO : FONT_BODY,
                textAlign: 'right', wordBreak: 'break-all', maxWidth: '70%',
              }}>
                {r.label === 'Order' && r.value.length > 22 ? `${r.value.slice(0, 10)}…${r.value.slice(-6)}` : r.value}
              </span>
            </div>
          ))}
        </div>

        {/* What happens */}
        <div style={{
          marginTop: 16, padding: '12px 14px',
          background: c.iB, borderRadius: 12,
          display: 'flex', alignItems: 'flex-start', gap: 8,
        }}>
          <ShieldAlert size={16} style={{ color: c.mu, flexShrink: 0, marginTop: 1 }} />
          <span style={{ fontSize: 13, color: c.mu, fontFamily: FONT_BODY, lineHeight: 1.45 }}>
            Your wallet will ask you to sign a message proving you own this order. No gas is spent and no
            funds move — this only authorizes the cancellation. The order&apos;s funds are returned to your
            wallet by the network.
          </span>
        </div>

        {/* Error / success states */}
        {status === 'error' && error && (
          <div style={{ marginTop: 14, fontSize: 13, color: c.eC, fontFamily: FONT_BODY, textAlign: 'center' }}>
            {error}
          </div>
        )}
        {status === 'success' && (
          <div style={{ marginTop: 14, fontSize: 13, color: c.ac, fontFamily: FONT_BODY, textAlign: 'center' }}>
            Cancellation requested — the order will finalize shortly.
          </div>
        )}

        {/* Action buttons */}
        <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
          {status === 'success' ? (
            <button
              onClick={onClose}
              style={{
                flex: 1, padding: '12px 16px', borderRadius: 14, border: 'none',
                background: 'linear-gradient(135deg, #00AAFF, #00CC66)', color: '#FFFFFF',
                fontSize: 15, fontWeight: 600, fontFamily: FONT_HEADING, cursor: 'pointer',
              }}
            >
              Done
            </button>
          ) : (
            <>
              <button
                onClick={onClose}
                disabled={busy}
                style={{
                  flex: 1, padding: '12px 16px', borderRadius: 14, border: `1px solid ${c.dv}`,
                  background: 'transparent', color: c.tx,
                  fontSize: 15, fontWeight: 600, fontFamily: FONT_HEADING,
                  cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1,
                }}
              >
                Back
              </button>
              <button
                onClick={status === 'error' ? onRetry : onConfirm}
                disabled={busy}
                style={{
                  flex: 2, padding: '12px 16px', borderRadius: 14, border: 'none',
                  background: c.eC, color: '#FFFFFF',
                  fontSize: 15, fontWeight: 600, fontFamily: FONT_HEADING,
                  cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.7 : 1,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                }}
              >
                {busy && (
                  <span style={{
                    width: 12, height: 12,
                    border: '2px solid currentColor', borderTopColor: 'transparent',
                    borderRadius: '50%', display: 'inline-block',
                    animation: 'spin 0.8s linear infinite',
                  }} />
                )}
                {status === 'signing' ? 'Waiting for signature…'
                  : status === 'submitting' ? 'Cancelling…'
                  : status === 'error' ? 'Try again'
                  : 'Confirm cancellation'}
              </button>
            </>
          )}
        </div>
      </div>
    </Modal>
  )
}
